'use client';

/**
 * ToolTimeoutCallback — "request a call-back" form of the tool-timeout
 * recovery flow (lib/tool-timeout-view.ts's ToolTimeoutView.CALLBACK), plus
 * the "details already on file" one-tap edge state layered on top of it.
 *
 * Two rendering branches, same component — mirrors handoff-timeout-
 * callback.tsx's existing `phoneOnFile` + internal `editing` toggle
 * precedent exactly (see that file's header comment), just with this
 * design's own, quite different copy for the on-file branch (own eyebrow/
 * title/description/actions, not just a swapped headline):
 *   - No phone on file, or user tapped "Use another number" -> the full
 *     form (name/phone/email/best-time).
 *   - Phone on file and not yet editing -> "Call you on ...3210?" one-tap
 *     confirm ("Yes, call me back" submits immediately with the number on
 *     file and the ANYTIME window — there's no time-picker on this minimal
 *     card).
 *
 * The phone field is REQUIRED here (unlike ContactInfoCard's optional one —
 * the backend needs a dialable number to actually place the call-back), so
 * an invalid number gets its own gentle, non-red treatment (amber border/
 * bg + reassuring copy) rather than ContactInfoCard's red error style, and
 * the submit button stays disabled until the number is dialable. Validity
 * itself is decided by lib/tool-timeout-phone.ts's isDialablePhone, which
 * wraps the shared contact-form-validation.ts check — only the microcopy
 * ("That looks a couple of digits short...") is bespoke to this card.
 *
 * Field ids are prefixed with React 19's `useId()` (ToolTimeoutCallbackForm
 * below), not hardcoded strings — this leaf can now be legitimately mounted
 * TWICE at once (the transcript's ToolTimeoutCard and the call surface's
 * CallToolTimeoutPanel both render off the SAME shared tool-timeout flow —
 * see useToolTimeoutFlow's header comment), and duplicate DOM ids are both
 * invalid HTML and a real accessibility bug: a `label`/`for` or
 * `aria-describedby` reference resolves against whichever element the
 * document-wide id index finds first, so a label tap on one surface could
 * focus the field on the OTHER surface, and a screen reader could announce
 * the wrong validation error.
 */
import { useId, useState } from 'react';
import { ChevronLeft, Lock, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { V3_COLORS } from '@/lib/design-tokens';
import { CallbackWindow } from '@/lib/api/support-requests';
import { isDialablePhone, maskPhoneLast4 } from '@/lib/tool-timeout-phone';
import { StandardCard } from './primitives/standard-card';

const CALLBACK_WINDOWS: { value: CallbackWindow; label: string }[] = [
  { value: CallbackWindow.ANYTIME, label: 'Anytime' },
  { value: CallbackWindow.MORNING, label: 'Morning' },
  { value: CallbackWindow.AFTERNOON, label: 'Afternoon' },
  { value: CallbackWindow.EVENING, label: 'Evening' },
];

/** Same deliberately-small, non-exhaustive set as handoff-timeout-
 *  callback.tsx — see that file's own note; not meant to be a general
 *  phone-input primitive. */
const COUNTRY_CODES = ['+1', '+44', '+91', '+61', '+971'];

interface ToolTimeoutCallbackSubmitValues {
  name: string;
  phone: string;
  email?: string;
  callbackWindow: CallbackWindow;
}

interface ToolTimeoutCallbackProps {
  phoneOnFile?: string;
  onBack: () => void;
  onSubmit: (values: ToolTimeoutCallbackSubmitValues) => void;
  submitting?: boolean;
  error?: string | null;
  /** Forwarded straight to the underlying StandardCard (both the one-tap
   *  confirm card AND the full form's card) — see tool-timeout-choose.tsx's
   *  identical prop for the full "why". Omitted by text chat. */
  className?: string;
}

export function ToolTimeoutCallback({
  phoneOnFile,
  onBack,
  onSubmit,
  submitting = false,
  error,
  className,
}: ToolTimeoutCallbackProps) {
  // No phone on file -> the full form IS the entry point. Phone on file ->
  // start on the one-tap confirm; "Use another number" reveals the form.
  const [editing, setEditing] = useState(!phoneOnFile);

  if (phoneOnFile && !editing) {
    return (
      <StandardCard
        icon={<Phone aria-hidden="true" className="size-[15px]" />}
        eyebrow="CALL-BACK"
        title={`Call you on ${maskPhoneLast4(phoneOnFile)}?`}
        className={className}
        actions={{
          primary: {
            label: 'Yes, call me back',
            onClick: () =>
              onSubmit({
                name: '',
                phone: phoneOnFile,
                callbackWindow: CallbackWindow.ANYTIME,
              }),
            disabled: submitting,
          },
          secondary: {
            label: 'Use another number',
            onClick: () => setEditing(true),
          },
        }}
      >
        {/* Copy review (governing principle: "Sara speaks in the user's
            world, never ours"): used to say "...as soon as the service is
            back", naming an internal service. "Back up and running"
            reused verbatim from tool-timeout-failed.tsx's own rewrite for
            the identical underlying event — see that file's copy-review
            note. */}
        We&apos;ve got your number from earlier. A live agent will call as soon
        as we&apos;re back up and running.
        {error && (
          <div
            role="alert"
            className="mt-[10px] text-[12.5px]"
            style={{ color: '#b3261e' }}
          >
            {error}
          </div>
        )}
      </StandardCard>
    );
  }

  return (
    <ToolTimeoutCallbackForm
      onBack={onBack}
      onSubmit={onSubmit}
      submitting={submitting}
      error={error}
      initialPhone={editing && phoneOnFile ? phoneOnFile : ''}
      className={className}
    />
  );
}

function ToolTimeoutCallbackForm({
  onBack,
  onSubmit,
  submitting,
  error,
  initialPhone,
  className,
}: {
  onBack: () => void;
  onSubmit: (values: ToolTimeoutCallbackSubmitValues) => void;
  submitting: boolean;
  error?: string | null;
  initialPhone: string;
  className?: string;
}) {
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState(initialPhone);
  const [email, setEmail] = useState('');
  const [callbackWindow, setCallbackWindow] = useState<CallbackWindow>(
    CallbackWindow.ANYTIME
  );
  const [touched, setTouched] = useState<{ name?: boolean; phone?: boolean }>(
    {}
  );
  // One stable id per MOUNTED instance (see file header) — this form can be
  // simultaneously on-screen twice (text chat + the call surface, driven by
  // the same shared flow), so a hardcoded id would collide across them.
  const uid = useId();
  const nameId = `${uid}-name`;
  const phoneId = `${uid}-phone`;
  const phoneErrorId = `${uid}-phone-error`;
  const emailId = `${uid}-email`;

  const trimmedName = name.trim();
  const trimmedPhone = phoneNumber.trim();
  const phoneValid = isDialablePhone(trimmedPhone);
  // Gentle amber only once there's actually something to say about it — a
  // still-empty, never-touched field isn't "invalid," it's just not
  // started yet.
  const showPhoneInvalid =
    touched.phone && trimmedPhone.length > 0 && !phoneValid;
  const showPhoneRequired = touched.phone && trimmedPhone.length === 0;

  const canSubmit = trimmedName.length > 0 && phoneValid && !submitting;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, phone: true });
    if (!canSubmit) return;
    onSubmit({
      name: trimmedName,
      phone: `${countryCode} ${trimmedPhone}`.trim(),
      email: email.trim() || undefined,
      callbackWindow,
    });
  };

  return (
    <StandardCard
      icon={<Phone aria-hidden="true" className="size-[15px]" />}
      eyebrow="REQUEST A CALL-BACK"
      title="Where can a live agent reach you?"
      className={className}
    >
      <form
        onSubmit={handleSubmit}
        noValidate
        className="flex flex-col gap-[14px]"
      >
        <div className="flex flex-col gap-1">
          <label
            htmlFor={nameId}
            className="text-[12.5px] font-semibold"
            style={{ color: V3_COLORS.muted }}
          >
            Name
          </label>
          <input
            id={nameId}
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
            disabled={submitting}
            aria-invalid={touched.name && trimmedName.length === 0}
            className={cn(
              'rounded-[11px] border-[1px] bg-[#FEFCF9] px-3.5 py-[11px] text-[14.5px]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e36001]/25',
              touched.name && trimmedName.length === 0
                ? 'border-[#b3261e]'
                : 'border-[#e6dccd] focus-visible:border-[#e36001]/60',
              'disabled:opacity-50'
            )}
            style={{ color: V3_COLORS.ink }}
          />
          {touched.name && trimmedName.length === 0 && (
            <span
              role="alert"
              className="text-[12px]"
              style={{ color: '#b3261e' }}
            >
              Name is required.
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label
            htmlFor={phoneId}
            className="text-[12.5px] font-semibold"
            style={{ color: V3_COLORS.muted }}
          >
            Phone
          </label>
          <div className="flex gap-[8px]">
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              disabled={submitting}
              aria-label="Country code"
              className="rounded-[11px] border-[1px] border-[#e6dccd] bg-[#FEFCF9] px-[14px] py-[11px] text-[14.5px] disabled:opacity-50"
              style={{ color: V3_COLORS.ink }}
            >
              {COUNTRY_CODES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
            <input
              id={phoneId}
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, phone: true }))}
              disabled={submitting}
              placeholder="Phone number"
              aria-invalid={showPhoneInvalid || showPhoneRequired}
              aria-describedby={
                showPhoneInvalid || showPhoneRequired ? phoneErrorId : undefined
              }
              className={cn(
                'flex-1 rounded-[11px] border-[1px] px-[14px] py-[11px] text-[14.5px] placeholder:text-[#b3a996]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e36001]/25',
                'disabled:opacity-50'
              )}
              style={{
                color: V3_COLORS.ink,
                // Gentle amber, deliberately not red — see this file's
                // header comment. The 3rd state (neither touched nor
                // invalid) falls through to the plain input treatment.
                borderColor: showPhoneInvalid ? V3_COLORS.amber : '#e6dccd',
                backgroundColor: showPhoneInvalid
                  ? V3_COLORS.amberInvalidBg
                  : '#FEFCF9',
              }}
            />
          </div>
          {showPhoneInvalid && (
            <span
              id={phoneErrorId}
              role="alert"
              className="text-[12px]"
              style={{ color: V3_COLORS.amberInvalidText }}
            >
              That looks a couple of digits short — mind checking it?
            </span>
          )}
          {showPhoneRequired && (
            <span
              id={phoneErrorId}
              role="alert"
              className="text-[12px]"
              style={{ color: '#b3261e' }}
            >
              Phone is required.
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label
            htmlFor={emailId}
            className="text-[12.5px] font-semibold"
            style={{ color: V3_COLORS.muted }}
          >
            Email
            <span
              className="ml-1 font-normal"
              style={{ color: V3_COLORS.faint }}
            >
              (optional)
            </span>
          </label>
          <input
            id={emailId}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            className="rounded-[11px] border-[1px] border-[#e6dccd] bg-[#FEFCF9] px-3.5 py-[11px] text-[14.5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e36001]/25 disabled:opacity-50"
            style={{ color: V3_COLORS.ink }}
          />
        </div>

        <div className="flex flex-col gap-[8px]">
          <span
            className="text-[12.5px] font-semibold"
            style={{ color: V3_COLORS.muted }}
          >
            Best time to call
          </span>
          <div className="flex flex-wrap gap-[8px]">
            {CALLBACK_WINDOWS.map((w) => {
              const selected = w.value === callbackWindow;
              return (
                <button
                  key={w.value}
                  type="button"
                  onClick={() => setCallbackWindow(w.value)}
                  aria-pressed={selected}
                  className={cn(
                    'min-h-[36px] rounded-[20px] px-[13px] py-[7px] text-[12.5px] font-semibold',
                    selected
                      ? 'border-[1px] border-[#E8C7A0] bg-[#FFF8EF]'
                      : 'border-[1px] border-[#eadfce] bg-white'
                  )}
                  style={{
                    color: selected ? V3_COLORS.ink : V3_COLORS.secondary,
                  }}
                >
                  {w.label}
                </button>
              );
            })}
          </div>
        </div>

        <div
          className="flex items-center gap-[6px] text-[12px]"
          style={{ color: V3_COLORS.standardCardMeta }}
        >
          <Lock aria-hidden="true" className="size-[11px]" />
          Name and number are required; email and time are optional.
        </div>

        {error && (
          <div
            role="alert"
            className="text-[12.5px]"
            style={{ color: '#b3261e' }}
          >
            {error}
          </div>
        )}

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onBack}
            disabled={submitting}
            className="flex min-h-[44px] items-center gap-1 text-[14px] font-semibold disabled:opacity-50"
            style={{ color: V3_COLORS.muted }}
          >
            <ChevronLeft aria-hidden="true" className="size-[14px]" />
            Back
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className={cn(
              'min-h-[44px] rounded-[22px] px-[18px] py-[10px] text-[13.5px] font-semibold text-white',
              'shadow-[0_10px_22px_-12px_rgba(227,96,1,0.7)]',
              canSubmit ? 'bg-[#E36001]' : 'cursor-not-allowed bg-[#E36001]/60'
            )}
          >
            {submitting ? 'Confirming…' : 'Confirm call-back'}
          </button>
        </div>
      </form>
    </StandardCard>
  );
}
